import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Dimmer,
  Loader,
  Table,
} from 'semantic-ui-react';
import qs from 'qs';
import { flashErrorMessage, flashSuccessMessage } from '../actions/flash';
import { fetchM } from '../actions/fetchM';
import ModalConfirm from '../containers/modals/ModalConfirm';
import Timestamp from './Timestamp';
import { namespaces, TRANSLATION_FILES } from '../constants/translation';

class TemplateList extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    where: PropTypes.shape({
      limit: PropTypes.number,
    }),
    loading: PropTypes.bool,
    type: PropTypes.oneOf(['print', 'sms', 'quotation']).isRequired,
    t: PropTypes.func,
  }
  constructor(props) {
    super(props);
    this.state = {
      count: 0,
      limit: 0,
      offset: 0,
      loading: props.loading || true,
      data: [],
      currentPage: 1,
      showConfirm: false,
      selectedTemplateID: '',
      showModalConfirm: false,
      token: '',
    };
  }
  componentDidMount() {
    this.fetchTemplates(this.props.where);
  }
  setTemplates(data = []) {
    return new Promise((res) => {
      this.setState({
        data: data || [],
      }, () => {
        res(data);
      });
    });
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  async fetchTemplates(where = {}) {
    await this.setLoading(true);
    const { store } = this.context;
    const { type, t } = this.props;
    const query = Object.assign({}, where);
    query.page = this.state.currentPage || 1;
    let res;
    let path = 'templates';
    if (type === 'sms') {
      path = 'sms_templates';
    } else {
      query.templateType = type;
    }
    try {
      res = await store.dispatch(fetchM(`/${path}/?${qs.stringify(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const count = parseInt(res.headers.get('M-Data-count'), 10);
      const limit = parseInt(res.headers.get('M-Data-limit'), 10);
      const offset = parseInt(res.headers.get('M-Data-offset'), 10) || 0;
      const data = await res.json();
      await this.setTemplates(data);
      // handle token.. etc
      const token = res.headers.get('X-Csrf-Token');
      await this.setComponentState({
        count,
        limit,
        offset,
        token,
      });
    }
    await this.setLoading(false);
  }
  handleChangePage = (currentPage) => {
    this.setState({ currentPage }, () => {
      this.fetchTemplates(this.props.where);
    });
  }
  handleCancelDelete = (e) => {
    e.preventDefault();
    this.setState({
      showConfirm: false,
      selectedTemplateID: '',
    });
  }
  handleConfirmDelete = async () => {
    const { t } = this.props;
    const { store } = this.context;
    const { selectedTemplateID, token } = this.state;
    await this.setLoading(true);

    try {
      await store.dispatch(fetchM(`/templates/${encodeURIComponent(selectedTemplateID)}/`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': token,
        },
      }));
    } catch (err) {
      Bugsnag.notifyException(err);
      if (err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      await this.setLoading(false);
      store.dispatch(flashErrorMessage(err));
    }

    this.setState({
      selectedTemplate: '',
      loading: false,
      showModalConfirm: false,
    }, () => {
      this.fetchTemplates(this.props.where);
      store.dispatch(flashSuccessMessage({ header: t('Delete Success', { ns: TRANSLATION_FILES.translation }), message: t('{{name}} successfully deleted', { ns: TRANSLATION_FILES.translation, name: selectedTemplateID }) }));
    });
  }
  handleOpenWarningModal = (e, templateID) => {
    if (e) { e.preventDefault(); }
    this.setState({
      selectedTemplateID: templateID,
      showModalConfirm: true,
    });
  }
  handleCloseWarningModal = (e) => {
    if (e) { e.preventDefault(); }
    this.setState({
      selectedTemplateID: '',
      showModalConfirm: false,
    });
  }
  isDefault = data => data.name && data.name.toLowerCase().includes('default');
  render() {
    const { t, type } = this.props;
    return (
      <div>
        <Dimmer
          inverted style={{ zIndex: 20 }}
          active={this.state.loading && this.state.data.length > 0}
        >
          <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
        </Dimmer>
        <ModalConfirm
          open={this.state.showModalConfirm}
          onClose={this.handleCloseWarningModal}
          onConfirm={this.handleConfirmDelete}
          headerNode={t('Delete Confirmation', { ns: TRANSLATION_FILES.screens })}
          content={t('Are you sure you want to delete this template?', { ns: TRANSLATION_FILES.screens })}
        />
        <Table
          striped unstackable
          selectable
        >
          <Table.Header fullWidth>
            <Table.Row>
              <Table.HeaderCell colSpan={5}>
                <Button
                  as={Link}
                  primary
                  size="small"
                  icon="add"
                  content={t('Template', { ns: TRANSLATION_FILES.screens })}
                  to={`/settings/${type}_templates/new/`}
                />
              </Table.HeaderCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell width={4}>{t('Name', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
              <Table.HeaderCell width={4}>ID</Table.HeaderCell>
              <Table.HeaderCell width={4}>{t('Updated At', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
              <Table.HeaderCell />
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {
              this.state.data.length > 0 ?
              this.state.data.map((field, key) => (
                <Table.Row key={key}>
                  <Table.Cell width={4}>
                    <Link
                      to={!this.isDefault(field) ? `/settings/${type}_templates/${encodeURIComponent(field.id)}/` : ''}
                    >
                      {field.name}
                    </Link>
                  </Table.Cell>
                  <Table.Cell width={4}>
                    <Link
                      to={!this.isDefault(field) ? `/settings/${type}_templates/${encodeURIComponent(field.id)}/` : ''}
                    >
                      {field.id}
                    </Link>
                  </Table.Cell>
                  <Table.Cell>
                    <Timestamp timestamp={field.updatedAt} format="MMMM Do YYYY, h:mm a" />
                  </Table.Cell>
                  <Table.Cell collapsing textAlign="right">
                    <Button
                      as={Link}
                      size="tiny"
                      icon="edit"
                      content="Edit"
                      to={`/settings/${type}_templates/${encodeURIComponent(field.id)}/`}
                      disabled={this.isDefault(field)}
                    />
                  </Table.Cell>
                  <Table.Cell collapsing textAlign="right">
                    {
                      type !== 'sms' && (
                        <Button
                          negative
                          size="tiny"
                          icon="trash"
                          onClick={e => this.handleOpenWarningModal(e, field.id)}
                          disabled={this.isDefault(field)}
                        />
                      )
                    }
                  </Table.Cell>
                </Table.Row>
              )) : (
                <Table.Row>
                  <Table.Cell colSpan={5} textAlign="center">
                    {
                      this.state.loading ?
                        <Loader active inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
                      : <p>{t('No templates found.', { ns: TRANSLATION_FILES.translation })}</p>
                    }
                  </Table.Cell>
                </Table.Row>
              )
            }
          </Table.Body>
        </Table>
      </div>
    );
  }
}

export default withTranslation(namespaces)(TemplateList);



// WEBPACK FOOTER //
// ./src/components/TemplateList.jsx